
import React, { createContext, useContext, useEffect, useState } from "react";
import i18n from "@/i18n";
import { LanguageDetectionService } from "@/services/languageDetectionService";

interface LanguageContextProps {
  language: string;
  setLanguage: (lang: string, isManualSelection?: boolean) => void;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState(i18n.language || 'en');
  const [isLoading, setIsLoading] = useState(true);

  const setLanguage = async (lang: string, isManualSelection: boolean = false) => {
    console.log(`🌐 Attempting to change language to: ${lang} (manual: ${isManualSelection})`);
    console.log(`🌐 Available languages in i18n:`, i18n.languages);
    console.log(`🌐 Current i18n language:`, i18n.language);
    
    try {
      await i18n.changeLanguage(lang);
      setLanguageState(lang);
      localStorage.setItem("language", lang);
      
      // Set flag only when user manually selects a language
      if (isManualSelection) {
        localStorage.setItem("language-manually-set", "true");
        console.log(`🎯 Language manually set by user: ${lang}`);
      }
      
      document.documentElement.setAttribute('lang', lang);
      
      console.log(`✅ Language successfully changed to: ${lang}`);
      console.log(`🌐 Updated i18n language:`, i18n.language);
    } catch (error) {
      console.error(`❌ Error changing language to ${lang}:`, error);
    }
  };

  useEffect(() => {
    console.log(`🚀 LanguageProvider initializing...`);
    console.log(`🌐 i18n isInitialized:`, i18n.isInitialized);
    console.log(`🌐 i18n hasLoadedNamespace:`, i18n.hasLoadedNamespace('common'));
    
    const initializeLanguage = async () => {
      // ADD TIMEOUT PROTECTION - Prevent infinite hang
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          console.warn('⚠️ i18n initialization timeout (5s), using fallback');
          resolve('timeout');
        }, 5000); // 5 second timeout
      });
      
      const initPromise = new Promise((resolve) => {
        if (!i18n.isInitialized) {
          console.log(`⏳ Waiting for i18n to initialize...`);
          i18n.on('initialized', resolve);
        } else {
          resolve('already-initialized');
        }
      });
      
      // Race between timeout and initialization
      const result = await Promise.race([initPromise, timeoutPromise]);
      console.log(`✅ i18n initialization result: ${result}`);
      
      try {
        // Use immediate browser language as primary fallback
        const browserLang = navigator.language.split('-')[0];
        const fallbackLang = i18n.languages.includes(browserLang) ? browserLang : 'en';
        
        // Set fallback immediately (non-blocking)
        await setLanguage(fallbackLang);
        console.log(`🎯 Using fallback language: ${fallbackLang}`);
        
        // Then try geolocation in background (non-blocking)
        LanguageDetectionService.detectLanguage()
          .then(detectedLanguage => {
            if (detectedLanguage && detectedLanguage !== fallbackLang) {
              console.log(`🌍 Geolocation detected different language: ${detectedLanguage}`);
              setLanguage(detectedLanguage);
            }
          })
          .catch((error) => {
            console.warn('⚠️ Geolocation detection failed (using fallback):', error);
            // Already using fallback, so no action needed
          });
      } catch (error) {
        console.error('❌ Error in language initialization:', error);
        // Final fallback to English
        await setLanguage('en');
      } finally {
        // CRITICAL: Always set isLoading to false to unblock render
        setIsLoading(false);
        console.log(`🎉 Language initialization complete`);
      }
    };

    initializeLanguage();

    // Listen for language changes from i18n directly
    const handleLanguageChange = (lng: string) => {
      console.log(`🔄 i18n language changed event: ${lng}`);
      setLanguageState(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
  return ctx;
};
